// ./react-app/src/components/DailyControllingClientFilter.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  useTheme,
  Divider,
  Typography,
  CircularProgress,
} from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';

export interface ClientOption {
  client_id: number;
  client_name: string;
  lead_price: number;
  customers: string[];
}

interface DailyControllingClientFilterProps {
  selectedClients: number[];
  onSelectionChange: (clients: number[]) => void;
  selectedCustomers: string[];
  startDate?: Date;
  endDate?: Date;
}

const customerMapping: Record<string, string> = {
  ft: 'FT',
  bg: 'BG',
  sw: 'SW',
  tt: 'TT',
  be: 'BE',
  foreign: 'Foreign',
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

const DailyControllingClientFilter: React.FC<DailyControllingClientFilterProps> = ({
  selectedClients,
  onSelectionChange,
  selectedCustomers,
  startDate,
  endDate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [allClients, setAllClients] = useState<ClientOption[]>([]);
  const [clientsWithData, setClientsWithData] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [initialSelectionDone, setInitialSelectionDone] = useState<boolean>(false);

  // Fetch client metadata
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const clientData = await fetchData<ClientOption[]>(
          '/api/daily-controlling/all-clients',
          {},
        );
        const flatClientData = Array.isArray(clientData) ? clientData.flat() : [];
        setAllClients(flatClientData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []); // Only run on mount

  // Fetch data to identify which clients have leads and auto-select
  useEffect(() => {
    const fetchDataAndAutoSelect = async () => {
      if (!startDate || !endDate || allClients.length === 0 || initialSelectionDone) {
        return;
      }

      try {
        // Fetch data to see which clients actually have leads
        const options = {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          customers: selectedCustomers.join(','),
        };

        const data = await fetchData<any[]>(
          '/api/daily-controlling/multi-customer-client-data',
          options,
        );

        // Identify clients with data
        const clientsWithLeads = new Set<number>();
        data.forEach((item) => {
          if (item.count > 0) {
            clientsWithLeads.add(item.client_id);
          }
        });
        setClientsWithData(clientsWithLeads);

        // Auto-select only paid clients that have data
        // Special cases:
        // - Always include "Spanische Leads Intern" (ID 401) for foreign leads
        // - Always include "Finanztrends" (ID 3) for main customer
        const paidClientsWithData = allClients
          .filter((client) => {
            const hasPaidPrice = parseFloat(String(client.lead_price)) > 0;
            const hasData = clientsWithLeads.has(client.client_id);
            const isSpanischeLeads = client.client_id === 401;
            const isFinanztrends = client.client_id === 3;

            // Include if: (paid AND has data) OR (is Spanische Leads with data) OR (is Finanztrends with data)
            return (
              (hasPaidPrice && hasData) ||
              (isSpanischeLeads && hasData) ||
              (isFinanztrends && hasData)
            );
          })
          .map((client) => client.client_id);

        onSelectionChange(paidClientsWithData);
        setInitialSelectionDone(true);
      } catch (error) {
        console.error('Error fetching client data:', error);
      }
    };

    fetchDataAndAutoSelect();
  }, [startDate, endDate, allClients, selectedCustomers, initialSelectionDone]);

  // Filter clients based on selected customers
  const filteredClients = allClients.filter((client) =>
    client.customers.some((customer) => selectedCustomers.includes(customer)),
  );

  // Group clients by customer for better organization
  const groupedClients = selectedCustomers.reduce(
    (acc, customer) => {
      acc[customer] = filteredClients.filter((client) => client.customers.includes(customer));
      return acc;
    },
    {} as Record<string, ClientOption[]>,
  );

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    onSelectionChange(typeof value === 'string' ? [] : value);
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      // If all are selected, deselect all
      onSelectionChange([]);
    } else {
      // If not all are selected, select all
      onSelectionChange(filteredClients.map((client) => client.client_id));
    }
  };

  const handleSelectPaidOnly = () => {
    const paidClients = filteredClients
      .filter((client) => parseFloat(String(client.lead_price)) > 0)
      .map((client) => client.client_id);
    onSelectionChange(paidClients);
  };

  const isAllSelected =
    selectedClients.length === filteredClients.length && filteredClients.length > 0;
  const isIndeterminate =
    selectedClients.length > 0 && selectedClients.length < filteredClients.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography>Loading clients...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 350, maxWidth: 450 }}>
      <FormControl fullWidth>
        <InputLabel id="client-multi-select-label">Kunden (Clients)</InputLabel>
        <Select
          labelId="client-multi-select-label"
          id="client-multi-select"
          multiple
          value={selectedClients}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label="Kunden (Clients)" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.slice(0, 3).map((clientId) => {
                const client = allClients.find((c) => c.client_id === clientId);
                if (!client) return null;
                const isPaid = parseFloat(String(client.lead_price)) > 0;
                return (
                  <Chip
                    key={clientId}
                    label={`${client.client_name} (${client.customers.map((c) => customerMapping[c] || c).join(', ')})`}
                    size="small"
                    sx={{
                      backgroundColor: isPaid ? colors.greenAccent[600] : colors.grey[600],
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                );
              })}
              {selected.length > 3 && (
                <Chip
                  label={`+${selected.length - 3} mehr`}
                  size="small"
                  sx={{
                    backgroundColor: colors.primary[500],
                    color: 'white',
                  }}
                />
              )}
            </Box>
          )}
          MenuProps={MenuProps}
          sx={{
            '& .MuiSelect-select': {
              minHeight: '56px',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {/* Action Options */}
          <MenuItem onClick={handleSelectAll} sx={{ fontWeight: 'bold' }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              sx={{
                color: colors.blueAccent[600],
                '&.Mui-checked': {
                  color: colors.blueAccent[600],
                },
                '&.MuiCheckbox-indeterminate': {
                  color: colors.blueAccent[600],
                },
              }}
            />
            <ListItemText primary={isAllSelected ? 'Alle abwählen' : 'Alle auswählen'} />
          </MenuItem>

          <MenuItem
            onClick={handleSelectPaidOnly}
            sx={{ fontWeight: 'bold', color: colors.greenAccent[600] }}
          >
            <Checkbox
              checked={false}
              sx={{
                color: colors.greenAccent[600],
              }}
            />
            <ListItemText primary="Nur bezahlte Clients" />
          </MenuItem>

          <Divider />

          {/* Grouped Clients */}
          {Object.entries(groupedClients).map(([customer, clients]) => (
            <Box key={customer}>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  fontWeight: 'bold',
                  backgroundColor: colors.primary[700],
                  color: colors.grey[100],
                }}
              >
                {customerMapping[customer] || customer}
              </Typography>
              {clients.map((client) => {
                const isPaid = parseFloat(String(client.lead_price)) > 0;
                const hasData = clientsWithData.has(client.client_id);

                // Determine color based on payment status and data availability
                let indicatorColor = colors.grey[600]; // Default: non-paid
                if (isPaid && hasData) {
                  indicatorColor = colors.greenAccent[600]; // Paid with data
                } else if (isPaid && !hasData) {
                  indicatorColor = colors.redAccent[400]; // Paid but no data
                }

                return (
                  <MenuItem key={client.client_id} value={client.client_id}>
                    <Checkbox
                      checked={selectedClients.includes(client.client_id)}
                      sx={{
                        color: indicatorColor,
                        '&.Mui-checked': {
                          color: indicatorColor,
                        },
                      }}
                    />
                    <ListItemText
                      primary={client.client_name}
                      secondary={
                        <span>
                          Lead-Preis: €{client.lead_price}
                          {hasData ? ' • Hat Daten' : ' • Keine Daten'}
                        </span>
                      }
                    />
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: indicatorColor,
                        borderRadius: '50%',
                        marginLeft: 1,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Box>
          ))}
        </Select>
      </FormControl>

      {/* Summary text */}
      <Box sx={{ mt: 1, fontSize: '0.875rem', color: colors.grey[100] }}>
        {selectedClients.length === 0
          ? 'Keine Clients ausgewählt'
          : selectedClients.length === filteredClients.length
            ? `Alle ${filteredClients.length} Clients ausgewählt`
            : `${selectedClients.length} von ${filteredClients.length} Clients ausgewählt`}
      </Box>
    </Box>
  );
};

export default DailyControllingClientFilter;
